import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Theme + font-size store. Three theme modes:
 *   • 'light'  — always light (default)
 *   • 'dark'   — always dark (CSS-filter inversion; see index.css)
 *   • 'auto'   — follow the OS `prefers-color-scheme` media query
 *
 * The value stored in localStorage IS the user's chosen mode (including
 * 'auto'); the applied `.dark` class is derived from that + the current
 * media query, and re-derived when the media query fires a change event
 * so a user who flips their OS dark mode at night sees the app follow.
 */
const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  effectiveTheme: 'light',
  fontSize: 'medium',
  setFontSize: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
};

const fontSizeMap = { small: '13px', medium: '16px', large: '20px' };

function _prefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function _resolveEffective(mode) {
  if (mode === 'auto') return _prefersDark() ? 'dark' : 'light';
  return mode === 'dark' ? 'dark' : 'light';
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('light');
  const [effectiveTheme, setEffectiveTheme] = useState('light');
  const [fontSize, setFontSizeState] = useState('medium');

  const applyEffective = useCallback((mode) => {
    const eff = _resolveEffective(mode);
    setEffectiveTheme(eff);
    document.documentElement.classList.toggle('dark', eff === 'dark');
    // color-scheme meta hint so form controls / scrollbars match too.
    document.documentElement.style.colorScheme = eff;
  }, []);

  // Boot — hydrate from localStorage exactly once.
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const savedFontSize = localStorage.getItem('fontSize') || 'medium';
    setThemeState(savedTheme);
    setFontSizeState(savedFontSize);
    document.documentElement.style.fontSize = fontSizeMap[savedFontSize] || '16px';
    applyEffective(savedTheme);
  }, [applyEffective]);

  // Listen for OS-level scheme changes while user is on 'auto'.
  useEffect(() => {
    if (theme !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => applyEffective('auto');
    // Modern browsers use addEventListener; Safari 13 needs addListener.
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, [theme, applyEffective]);

  const updateTheme = useCallback((newTheme) => {
    const normalised = ['light', 'dark', 'auto'].includes(newTheme) ? newTheme : 'light';
    setThemeState(normalised);
    localStorage.setItem('theme', normalised);
    applyEffective(normalised);
  }, [applyEffective]);

  const updateFontSize = useCallback((newSize) => {
    document.documentElement.style.fontSize = fontSizeMap[newSize] || '16px';
    setFontSizeState(newSize);
    localStorage.setItem('fontSize', newSize);
  }, []);

  const value = useMemo(() => ({
    theme,
    setTheme: updateTheme,
    effectiveTheme,
    fontSize,
    setFontSize: updateFontSize,
  }), [theme, updateTheme, effectiveTheme, fontSize, updateFontSize]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
